import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subscription,
} from 'rxjs';
import { RoutingService } from '../../../core/services/routing.service';
import { FormControl } from '@angular/forms';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { PackingBoxViewApiService } from '../api/view.service';
import {
  PackingBoxTablePetitionRow,
  PackingBoxTableRow,
} from '../../../common/packing-box/models/table-row';
import { MatDialog } from '@angular/material/dialog';
import {
  PackingBoxCustomLabelComponent,
  PackingBoxCustomLabelType,
} from '../dialog/custom-label/custom-label.component';
import { AlertService } from '../../../core/services/alert.service';
import { PackingBoxCreateLabelComponent } from '../dialog/create-label/create-label.component';
import { PackingBoxCreateLabelType } from '../types/create-label-dialog';
import {
  PackingBoxCustomPrinterComponent,
  PackingBoxCustomPrinterType,
} from '../dialog/custom-printer/custom-printer.component';
import { PACKING_BOX_SPECIFY_STATION } from '../tokens/printer-station';
import {
  PrinterSessionService,
} from '../../../core/services/printers/printer-session.service';
import { ApiRouteLabelService } from '../../../api/elite/routes/label.service';
import { PrinterDepartment } from '../../../api/elite/types/printer-department';

type DepartmentType =
  | 'Compression Molding'
  | 'Polyurethane'
  | 'Injection Molding'
  | 'Filters'
  | 'Hardware Pack'
  | 'Plastics';

@Injectable()
export class PackingBoxViewService {
  controlSelectDepartment = new FormControl<DepartmentType>(undefined);
  protected readonly apiRouter = inject(PackingBoxViewApiService);
  protected readonly routerService = inject(RoutingService);
  routerDepartment$ = this.routerService
    .lastRouteData<DepartmentType>('department')
    .pipe(startWith(''), distinctUntilChanged());
  showControl$ = this.routerDepartment$.pipe(
    map((val) => {
      return !(val && val !== '');
    })
  );
  brandType$ = this.routerService
    .lastRouteData<'sb' | 'daystar'>('companyType')
    .pipe(distinctUntilChanged());
  protected readonly dialog = inject(MatDialog);
  protected readonly alertService = inject(AlertService);
  protected readonly specifyStation = inject(PACKING_BOX_SPECIFY_STATION);
  protected readonly printerSessionService = inject(PrinterSessionService);
  protected readonly labelService = inject(ApiRouteLabelService);
  protected readonly _automaticPrinting = new BehaviorSubject<boolean>(false);
  automaticPrinting$ = this._automaticPrinting.asObservable();
  protected autoPrintingSub: Subscription;
  protected readonly _printerName = new BehaviorSubject<string>(null);
  printerName$ = this._printerName.asObservable();
  private selectionDepartment$ = combineLatest([
    this.routerDepartment$,
    this.controlSelectDepartment.valueChanges.pipe(
      startWith(''),
      distinctUntilChanged()
    ),
  ]).pipe(
    distinctUntilChanged(),
    map(([dep1, dep2]) => {
      if (dep1 && dep1 !== '') {
        return dep1;
      } else if (dep2 && dep2 !== '') {
        return dep2;
      }
      return undefined;
    })
  );
  // selectingSalesOrder$ = this.routerService
  //   .lastRouteData<boolean>('selectingSalesOrder')
  //   .pipe(startWith(false), distinctUntilChanged());
  dataPackingBox$: Observable<PackingBoxTableRow[]> =
    this.selectionDepartment$.pipe(
      switchMap((dep) => {
        if (dep) {
          return this.apiRouter.data(dep);
        }
        return of([]);
      })
    );
  private addedPetitions = new BehaviorSubject<PackingBoxTablePetitionRow[]>(
    []
  );
  dataPetitions$: Observable<PackingBoxTablePetitionRow[]> = combineLatest([
    this.selectionDepartment$.pipe(
      switchMap((dep) => {
        if (dep) {
          return this.apiRouter.getDataOfPetitions(dep);
        }
        return of([]);
      }),
      tap(() => this.addedPetitions.next([]))
    ),
    this.addedPetitions.asObservable(),
  ]).pipe(
    map(([data, addedPetitions]) => {
      return [...data, ...addedPetitions];
    })
  );

  constructor() {}

  set automaticPrinting(value: boolean) {
    this._automaticPrinting.next(value);
  }

  set printerName(value: string) {
    this._printerName.next(value);
  }

  createCustomLabel(): void {
    this.routerDepartment$
      .pipe(
        switchMap((dep) => this.apiRouter.dataToCreatePetition(dep)),
        switchMap((res) =>
          this.dialog
            .open(PackingBoxCustomLabelComponent, {
              data: {
                ...res,
                type: 'create',
              },
            })
            .afterClosed()
        ),
        switchMap((res: PackingBoxCustomLabelType['response']) => {
          return this.apiRouter.createPetition(res);
        })
      )
      .subscribe((data) => {
        this.addedPetitions.next([...this.addedPetitions.value, data]);
      });
  }

  setAutomaticPrinting(): void {
    if (this.autoPrintingSub) {
      return;
    }
    this.autoPrintingSub = combineLatest([
      this.brandType$,
      this.selectionDepartment$,
    ])
      .pipe(
        take(1),
        switchMap(([brand, dep]) =>
          this.handleAutomaticStationPrinting(brand, dep)
        )
      )
      .subscribe({
        next: () => {},
        error: (err) => {
          this.alertService.error(err, 'No se pudo imprimir');
        },
        complete: () => {
          // En el caso que no se seleccione ningun nombre de la impresora, volver a ponerlo como falso.
          this.automaticPrinting = false;
          // Reiniciar el nombre de la impresora
          this._printerName.next(null);
        },
      });
  }

  createNamePrinter(): Observable<PackingBoxCustomPrinterType['form']> {
    return this.dialog
      .open(PackingBoxCustomPrinterComponent, {
        data: {},
        minWidth: '420px',
      })
      .afterClosed()
      .pipe(
        filter((data) => !!data),
        map((data: PackingBoxCustomPrinterType['form']) => {
          return data;
        })
      );
  }

  removeAutomaticPrinting(disconnect = false): void {
    if (this.autoPrintingSub) {
      this.autoPrintingSub.unsubscribe();
      this.autoPrintingSub = undefined;
    }
    if (disconnect){
      this.apiRouter.closeConnection();
      this.printerSessionService.clear();
    }
    // Reset the printer name
    this.printerName = '';
  }

  createLabelData(row: PackingBoxTableRow): Observable<{
    salesOrder: string;
    purchaseOrder?: string;
    partNumberByCustomer: string | null;
    description: string;
    salesOrderLineId?: number | null;
    quantity: number;
    numLabels: number;
    lotNumberSelected?: string;
    itemName?: string;
    salesOrderToInsert?: any | null;
  } | null> {
    return this.apiRouter
      .getPrioritySaleOrderLine(row.salesOrderLineId, row.netsuiteItemId)
      .pipe(
        catchError(() => of(undefined)),
        map((lineObj) => {
          const safe = lineObj ?? {};

          return {
            ...(safe as any),
            partNumberByCustomer: lineObj?.partNumberByCustomer ?? null,
            salesOrder: lineObj?.salesOrder ?? 'Stock',

            description: row.itemDescription,
          };
        }),
        switchMap((data) =>
          this.apiRouter.getSpecificLotNumber(row.transactionNumber).pipe(
            map(({ hasInventoryNumber, lot }) => {
              if (hasInventoryNumber && (lot?.length ?? 0) <= 0) {
                throw new Error(
                  'Esta work order necesita tener un lote especificado'
                );
              }

              return {
                ...(data ?? {}),
                lotNumbers: (lot?.length ?? 0) > 0 ? [lot] : [],
              } as any;
            }),
            catchError(() =>
              of({
                ...(data ?? {}),
                lotNumbers: [],
              } as any)
            )
          )
        ),
        withLatestFrom(
          this.routerDepartment$.pipe(
            filter((dept) => !!dept && dept.trim() !== ''),
            distinctUntilChanged()
          )
        ),
        switchMap(([data, department]) =>
          this.dialog
            .open(PackingBoxCreateLabelComponent, {
              data: {
                maxQuantitySO: data.quantity,
                maxQuantity: row.quantity,
                isToStock: data.salesOrder === 'Stock',
                lotNumbers: data.lotNumbers ?? [],
                salesOrder: data.salesOrder,
                data,
                department,
              },
            })
            .afterClosed()
            .pipe(
              map((res: PackingBoxCreateLabelType['response']) => {
                if (!res) {
                  return null;
                }

                return {
                  ...data,
                  ...res,
                  partNumberByCustomer: res.addSalesOrder
                    ? data.partNumberByCustomer
                    : null,
                  salesOrder: res.addSalesOrder ? data.salesOrder : 'Stock',
                  description: data.description,
                  salesOrderToInsert: res.addSalesOrder
                    ? data.salesOrderToInsert
                    : null,
                  salesOrderLineId: res.addSalesOrder
                    ? data.salesOrderLineId ?? null
                    : null,
                };
              })
            )
        ),
        switchMap((res) =>
          res?.salesOrderToInsert
            ? this.apiRouter.addSalesOrders(res.salesOrderToInsert).pipe(
                map((tran) => ({
                  ...res,
                  salesOrderLineId: tran.lines.find(
                    (el) =>
                      el.lineId ===
                      (Array.isArray(res.salesOrderToInsert.line)
                        ? res.salesOrderToInsert.line[0].lineId
                        : res.salesOrderToInsert.line.lineId)
                  ).id,
                }))
              )
            : of(res)
        )
      );
  }

  /**
   *
   * @param brand
   * @param dep
   * @description Funcion que sirve para definir como se manejara la forma en la que
   * imprimira dentro de la estacion.
   * Por el momento existen dos opciones:
   * - Impresion automatica por departamento:
   * Donde se imprimira lo que venga de un departamento, solo escuchando lo que venga del
   * departamento adecuado.
   * - Impresion automatica por departamento y nombrado:
   * Donde se imprimira lo que venga de un departamento, pero tambien se podra definir un nombre
   * de impresora para imprimir. Permitiendo tener mas de 1 estacion funcional para impresion
   * @private
   */
  private handleAutomaticStationPrinting(
    brand: 'sb' | 'daystar',
    dep: string
  ): Observable<any> {
    if (this.specifyStation) {
      const printerName = this._printerName.value;
      if (printerName) {
        return this.apiRouter.printBasicWorkOrders(
          brand,
          dep,
          printerName
        );
      }
      return this.createNamePrinter().pipe(
        switchMap(({ namePrinter }) => {
          if (namePrinter) {
            // Set the printer name
            this.settingUpPrinter(namePrinter, dep);
            return this.apiRouter.printBasicWorkOrders(brand, dep, namePrinter);
          }
          return of('CANCELLED');
        })
      );
    }
    return this.apiRouter.printBasicWorkOrders(brand, dep);
  }

  private settingUpPrinter(namePrinter: string, dep: string): void {
    this.printerName = namePrinter;
    this.printerSessionService.set(namePrinter, dep);
  }

  loadPrinters(): Observable<string> {
    return this.selectionDepartment$
      .pipe(
        take(1),
        switchMap((department) =>
          this.labelService
            .sharedPrintersByDepartment(department)
            .pipe(map((printers) => ({ printers, department })))
        ),
        map((res) => this.handlePrinterSelection(res.printers, res.department)),
      );
  }

  private handlePrinterSelection(
    printers: PrinterDepartment[],
    department: string
  ): string {
    const session = this.printerSessionService.snapshot;
    const printersDepartment = printers.filter(
      (printer) => printer.department === department
    );
    // restore session
    if (
      session &&
      session.department === department &&
      printersDepartment.find(
        (op) => `${op.department}-${op.printerName}` === session.id
      )
    ) {
      console.log('Restoring previous printer:', session.printerName);
      this.printerName = session.printerName;
      this.automaticPrinting = true;
      return session.printerName;
    }

    // only one printer available
    // if (printersDepartment.length === 1) {
    //   this.selectPrinter(printersDepartment[0]);
    //   return printersDepartment[0].printerName;
    // }

    // cleanup
    this.printerSessionService.clear();
    this.automaticPrinting = false;
    return;
  }

  selectPrinter(printer: PrinterDepartment): void {
    this.printerName = printer.printerName;
    this.printerSessionService.set(printer.printerName, printer.department);
    this.automaticPrinting = true;
  }
}
